// src/utils/kitchenModel.js

/*
 * MeasureSmart Kitchen Model Generator
 *
 * Converts authoritative measured kitchen geometry into a
 * conceptual cabinet / countertop / appliance layout.
 *
 * IMPORTANT:
 * - Wall measurements remain authoritative.
 * - Cabinetry is generated / conceptual.
 * - Generated objects must remain inside the measured wall envelope.
 * - Standard cabinet widths are preferred over arbitrary widths.
 * - Small remaining spaces become fillers rather than stretched cabinets.
 * - Appliance openings are explicitly reserved.
 * - Tall/pantry cabinetry is only generated when BOTH horizontal
 *   and vertical space are sufficient.
 */

/* =========================================================
   GLOBAL DIMENSIONS
   ========================================================= */

const DEFAULT_WALL_HEIGHT_FT = 8;

const BASE_DEPTH_FT = 2; // 24"
const BASE_HEIGHT_FT = 2.875; // 34.5"

const COUNTER_HEIGHT_FT = 3; // 36"
const COUNTER_THICKNESS_FT = 0.125; // 1.5"

const WALL_CABINET_DEPTH_FT = 1; // 12"
const WALL_CABINET_HEIGHT_FT = 2.5; // 30"
const WALL_CABINET_BOTTOM_FT = 4.5; // 54"

const RANGE_WIDTH_FT = 2.5; // 30"
const SINK_WIDTH_FT = 2.5; // 30"

const TOP_CLEARANCE_FT = 0.25; // 3"
const MIN_WALL_CABINET_HEIGHT_FT = 1.5; // 18"

const TALL_CABINET_DEPTH_FT = 2; // 24"
const TALL_CABINET_WIDTH_FT = 2; // 24"
const MIN_TALL_CABINET_HEIGHT_FT = 6; // 72"

const CORNER_RESERVE_FT = BASE_DEPTH_FT;

const MIN_FILLER_FT = 0.125; // 1.5"
const MAX_FILLER_FT = 0.5; // 6"

const STANDARD_CABINET_WIDTHS_FT = [
  3,     // 36"
  2.75,  // 33"
  2.5,   // 30"
  2,     // 24"
  1.75,  // 21"
  1.5,   // 18"
  1.25,  // 15"
  1,     // 12"
];

/* =========================================================
   PUBLIC ENTRY
   ========================================================= */

export function buildKitchenModel(geometry) {
  if (!geometry?.walls?.length) {
    return emptyModel();
  }

  if (geometry.layout === "l-shape") {
    return buildLShapeModel(geometry);
  }

  if (geometry.layout === "u-shape") {
    return buildUShapeModel(geometry);
  }

  return emptyModel();
}

function emptyModel() {
  return {
    cabinets: [],
    fillers: [],
    countertops: [],
    appliances: [],
    fixtures: [],
  };
}

/* =========================================================
   WALL HELPERS
   ========================================================= */

function getWall(geometry, id) {
  return geometry.walls.find((wall) => wall.id === id);
}

function getWallHeight(geometry, wallId) {
  const wall = getWall(geometry, wallId);
  const height = Number(wall?.heightFt);

  if (Number.isFinite(height) && height > 0) {
    return height;
  }

  return DEFAULT_WALL_HEIGHT_FT;
}

/* =========================================================
   HEIGHT RULES
   ========================================================= */

function getUpperCabinetHeight(wallHeightFt) {
  const availableHeight =
    wallHeightFt -
    WALL_CABINET_BOTTOM_FT -
    TOP_CLEARANCE_FT;

  // Never force an upper cabinet into an impossible wall envelope.
  if (availableHeight < MIN_WALL_CABINET_HEIGHT_FT) {
    return null;
  }

  return Math.min(
    WALL_CABINET_HEIGHT_FT,
    availableHeight
  );
}

function getTallCabinetHeight(wallHeightFt) {
  const availableHeight =
    wallHeightFt - TOP_CLEARANCE_FT;

  if (availableHeight < MIN_TALL_CABINET_HEIGHT_FT) {
    return null;
  }

  return availableHeight;
}

function supportsBaseCabinet(wallHeightFt) {
  return wallHeightFt >= BASE_HEIGHT_FT;
}

function supportsCountertop(wallHeightFt) {
  return wallHeightFt >= COUNTER_HEIGHT_FT;
}

/* =========================================================
   STANDARD CABINET PACKING
   ========================================================= */

function packStandardCabinets(runLengthFt) {
  const runIn = Math.max(
    0,
    Math.round(runLengthFt * 12)
  );

  const widthsIn = STANDARD_CABINET_WIDTHS_FT.map(
    (width) => Math.round(width * 12)
  );

  if (runIn < Math.min(...widthsIn)) {
    return {
      modules: [],
      usedFt: 0,
      fillerFt: runIn / 12,
    };
  }

  const best = Array(runIn + 1).fill(null);
  best[0] = [];

  for (let used = 0; used <= runIn; used += 1) {
    if (best[used] == null) continue;

    widthsIn.forEach((width) => {
      const next = used + width;
      if (next > runIn) return;

      const candidate = [
        ...best[used],
        width,
      ];

      if (
        best[next] == null ||
        candidate.length < best[next].length
      ) {
        best[next] = candidate;
      }
    });
  }

  let bestUsed = 0;
  let bestModules = [];

  for (let used = 0; used <= runIn; used += 1) {
    if (best[used] == null) continue;

    if (used > bestUsed) {
      bestUsed = used;
      bestModules = best[used];
      continue;
    }

    if (
      used === bestUsed &&
      best[used].length < bestModules.length
    ) {
      bestModules = best[used];
    }
  }

  bestModules.sort((a, b) => b - a);

  return {
    modules: bestModules.map(
      (widthIn) => widthIn / 12
    ),
    usedFt: bestUsed / 12,
    fillerFt: Math.max(
      0,
      (runIn - bestUsed) / 12
    ),
  };
}

/* =========================================================
   RUN LAYOUT
   ========================================================= */

function createRunLayout(runLengthFt) {
  if (!runLengthFt || runLengthFt <= 0) {
    return {
      modules: [],
      fillerStartFt: 0,
      fillerEndFt: 0,
      usedFt: 0,
    };
  }

  const packed = packStandardCabinets(runLengthFt);

  let fillerStartFt = packed.fillerFt / 2;
  let fillerEndFt = packed.fillerFt / 2;

  if (fillerStartFt < MIN_FILLER_FT / 2) {
    fillerStartFt = 0;
  }

  if (fillerEndFt < MIN_FILLER_FT / 2) {
    fillerEndFt = 0;
  }

  const modules = [];
  let cursor = fillerStartFt;

  packed.modules.forEach((widthFt, index) => {
    modules.push({
      index,
      widthFt,
      offsetFt: cursor + widthFt / 2,
    });

    cursor += widthFt;
  });

  return {
    modules,
    fillerStartFt,
    fillerEndFt,
    usedFt: packed.usedFt,
  };
}

/* =========================================================
   FILLER HELPERS
   ========================================================= */

function addBackFiller({
  fillers,
  id,
  startFt,
  widthFt,
  heightFt,
  depthFt,
  y,
}) {
  if (widthFt < MIN_FILLER_FT) return;

  fillers.push({
    id,
    type: "filler",
    wall: "back",

    widthFt: Math.min(
      widthFt,
      MAX_FILLER_FT
    ),

    depthFt,
    heightFt,

    position: [
      startFt + widthFt / 2,
      y,
      depthFt / 2,
    ],

    rotation: [0, 0, 0],
    frontDirection: "front",
    generated: true,
  });
}

function addSideFiller({
  fillers,
  id,
  wall,
  xPosition,
  startFt,
  widthFt,
  heightFt,
  depthFt,
  y,
  frontDirection,
}) {
  if (widthFt < MIN_FILLER_FT) return;

  fillers.push({
    id,
    type: "filler",
    wall,

    widthFt: Math.min(
      widthFt,
      MAX_FILLER_FT
    ),

    depthFt,
    heightFt,

    position: [
      xPosition,
      y,
      startFt + widthFt / 2,
    ],

    rotation: [
      0,
      Math.PI / 2,
      0,
    ],

    frontDirection,
    generated: true,
  });
}

/* =========================================================
   BACK BASE CABINET RUN
   ========================================================= */

function addBackBaseModules({
  cabinets,
  fillers,
  idPrefix,
  startFt,
  runLengthFt,
  wallHeightFt,
}) {
  if (!supportsBaseCabinet(wallHeightFt)) {
    return;
  }

  const layout = createRunLayout(runLengthFt);

  layout.modules.forEach((module) => {
    cabinets.push({
      id: `${idPrefix}-${module.index}`,
      type: "base",
      wall: "back",

      widthFt: module.widthFt,
      depthFt: BASE_DEPTH_FT,
      heightFt: BASE_HEIGHT_FT,

      position: [
        startFt + module.offsetFt,
        BASE_HEIGHT_FT / 2,
        BASE_DEPTH_FT / 2,
      ],

      rotation: [0, 0, 0],
      frontDirection: "front",
      generated: true,

      standardWidthIn: Math.round(
        module.widthFt * 12
      ),
    });
  });

  if (layout.fillerStartFt >= MIN_FILLER_FT) {
    addBackFiller({
      fillers,
      id: `${idPrefix}-filler-start`,
      startFt,
      widthFt: layout.fillerStartFt,
      heightFt: BASE_HEIGHT_FT,
      depthFt: BASE_DEPTH_FT,
      y: BASE_HEIGHT_FT / 2,
    });
  }

  if (layout.fillerEndFt >= MIN_FILLER_FT) {
    addBackFiller({
      fillers,
      id: `${idPrefix}-filler-end`,
      startFt:
        startFt +
        runLengthFt -
        layout.fillerEndFt,
      widthFt: layout.fillerEndFt,
      heightFt: BASE_HEIGHT_FT,
      depthFt: BASE_DEPTH_FT,
      y: BASE_HEIGHT_FT / 2,
    });
  }
}

/* =========================================================
   BACK UPPER CABINET RUN
   ========================================================= */

function addBackUpperModules({
  cabinets,
  fillers,
  idPrefix,
  startFt,
  runLengthFt,
  wallHeightFt,
}) {
  const upperHeight =
    getUpperCabinetHeight(wallHeightFt);

  if (upperHeight == null) return;

  const layout = createRunLayout(runLengthFt);

  const upperY =
    WALL_CABINET_BOTTOM_FT +
    upperHeight / 2;

  layout.modules.forEach((module) => {
    cabinets.push({
      id: `${idPrefix}-${module.index}`,
      type: "wall",
      wall: "back",

      widthFt: module.widthFt,
      depthFt: WALL_CABINET_DEPTH_FT,
      heightFt: upperHeight,

      position: [
        startFt + module.offsetFt,
        upperY,
        WALL_CABINET_DEPTH_FT / 2,
      ],

      rotation: [0, 0, 0],
      frontDirection: "front",
      generated: true,

      standardWidthIn: Math.round(
        module.widthFt * 12
      ),

      heightSource:
        "derived-from-wall-height",
    });
  });

  if (layout.fillerStartFt >= MIN_FILLER_FT) {
    addBackFiller({
      fillers,
      id: `${idPrefix}-filler-start`,
      startFt,
      widthFt: layout.fillerStartFt,
      heightFt: upperHeight,
      depthFt: WALL_CABINET_DEPTH_FT,
      y: upperY,
    });
  }

  if (layout.fillerEndFt >= MIN_FILLER_FT) {
    addBackFiller({
      fillers,
      id: `${idPrefix}-filler-end`,
      startFt:
        startFt +
        runLengthFt -
        layout.fillerEndFt,
      widthFt: layout.fillerEndFt,
      heightFt: upperHeight,
      depthFt: WALL_CABINET_DEPTH_FT,
      y: upperY,
    });
  }
}

/* =========================================================
   SIDE BASE CABINET RUN
   ========================================================= */

function addSideBaseModules({
  cabinets,
  fillers,
  idPrefix,
  wall,
  xPosition,
  startFt,
  runLengthFt,
  frontDirection,
  wallHeightFt,
}) {
  if (!supportsBaseCabinet(wallHeightFt)) {
    return;
  }

  const layout = createRunLayout(runLengthFt);

  layout.modules.forEach((module) => {
    cabinets.push({
      id: `${idPrefix}-${module.index}`,
      type: "base",
      wall,

      widthFt: module.widthFt,
      depthFt: BASE_DEPTH_FT,
      heightFt: BASE_HEIGHT_FT,

      position: [
        xPosition,
        BASE_HEIGHT_FT / 2,
        startFt + module.offsetFt,
      ],

      rotation: [
        0,
        Math.PI / 2,
        0,
      ],

      frontDirection,
      generated: true,

      standardWidthIn: Math.round(
        module.widthFt * 12
      ),
    });
  });

  if (layout.fillerStartFt >= MIN_FILLER_FT) {
    addSideFiller({
      fillers,
      id: `${idPrefix}-filler-start`,
      wall,
      xPosition,
      startFt,
      widthFt: layout.fillerStartFt,
      heightFt: BASE_HEIGHT_FT,
      depthFt: BASE_DEPTH_FT,
      y: BASE_HEIGHT_FT / 2,
      frontDirection,
    });
  }

  if (layout.fillerEndFt >= MIN_FILLER_FT) {
    addSideFiller({
      fillers,
      id: `${idPrefix}-filler-end`,
      wall,
      xPosition,
      startFt:
        startFt +
        runLengthFt -
        layout.fillerEndFt,
      widthFt: layout.fillerEndFt,
      heightFt: BASE_HEIGHT_FT,
      depthFt: BASE_DEPTH_FT,
      y: BASE_HEIGHT_FT / 2,
      frontDirection,
    });
  }
}

/* =========================================================
   SIDE UPPER CABINET RUN
   ========================================================= */

function addSideUpperModules({
  cabinets,
  fillers,
  idPrefix,
  wall,
  xPosition,
  startFt,
  runLengthFt,
  frontDirection,
  wallHeightFt,
}) {
  const upperHeight =
    getUpperCabinetHeight(wallHeightFt);

  if (upperHeight == null) return;

  const layout = createRunLayout(runLengthFt);

  const upperY =
    WALL_CABINET_BOTTOM_FT +
    upperHeight / 2;

  layout.modules.forEach((module) => {
    cabinets.push({
      id: `${idPrefix}-${module.index}`,
      type: "wall",
      wall,

      widthFt: module.widthFt,
      depthFt: WALL_CABINET_DEPTH_FT,
      heightFt: upperHeight,

      position: [
        xPosition,
        upperY,
        startFt + module.offsetFt,
      ],

      rotation: [
        0,
        Math.PI / 2,
        0,
      ],

      frontDirection,
      generated: true,

      standardWidthIn: Math.round(
        module.widthFt * 12
      ),

      heightSource:
        "derived-from-wall-height",
    });
  });

  if (layout.fillerStartFt >= MIN_FILLER_FT) {
    addSideFiller({
      fillers,
      id: `${idPrefix}-filler-start`,
      wall,
      xPosition,
      startFt,
      widthFt: layout.fillerStartFt,
      heightFt: upperHeight,
      depthFt: WALL_CABINET_DEPTH_FT,
      y: upperY,
      frontDirection,
    });
  }

  if (layout.fillerEndFt >= MIN_FILLER_FT) {
    addSideFiller({
      fillers,
      id: `${idPrefix}-filler-end`,
      wall,
      xPosition,
      startFt:
        startFt +
        runLengthFt -
        layout.fillerEndFt,
      widthFt: layout.fillerEndFt,
      heightFt: upperHeight,
      depthFt: WALL_CABINET_DEPTH_FT,
      y: upperY,
      frontDirection,
    });
  }
}

/* =========================================================
   COUNTERTOPS

   IMPORTANT:
   Counters now carry wall + frontDirection metadata.

   Kitchen3DView uses this metadata to place decorative
   backsplash surfaces against the correct measured wall.
   This prevents side-wall backsplash geometry from appearing
   as a floating panel inside U-shaped kitchens.
   ========================================================= */

function addBackCounter({
  countertops,
  id,
  startFt,
  runLengthFt,
}) {
  if (runLengthFt <= 0) return;

  countertops.push({
    id,

    wall: "back",
    frontDirection: "front",

    widthFt: runLengthFt,
    depthFt: BASE_DEPTH_FT + 0.08,
    thicknessFt: COUNTER_THICKNESS_FT,

    position: [
      startFt + runLengthFt / 2,
      COUNTER_HEIGHT_FT,
      BASE_DEPTH_FT / 2,
    ],

    rotation: [0, 0, 0],
  });
}

function addSideCounter({
  countertops,
  id,
  wall,
  frontDirection,
  xPosition,
  startFt,
  runLengthFt,
}) {
  if (runLengthFt <= 0) return;

  countertops.push({
    id,

    wall,
    frontDirection,

    widthFt: runLengthFt,
    depthFt: BASE_DEPTH_FT + 0.08,
    thicknessFt: COUNTER_THICKNESS_FT,

    position: [
      xPosition,
      COUNTER_HEIGHT_FT,
      startFt + runLengthFt / 2,
    ],

    rotation: [
      0,
      Math.PI / 2,
      0,
    ],
  });
}

/* =========================================================
   PANTRY HELPERS
   ========================================================= */

function canFitPantry({
  runLengthFt,
  wallHeightFt,
}) {
  const tallHeight =
    getTallCabinetHeight(wallHeightFt);

  if (tallHeight == null) {
    return false;
  }

  return (
    runLengthFt >=
    TALL_CABINET_WIDTH_FT + 1
  );
}

function createBackTallCabinet({
  id,
  xPosition,
  wallHeightFt,
}) {
  const heightFt =
    getTallCabinetHeight(wallHeightFt);

  if (heightFt == null) return null;

  return {
    id,
    type: "tall",
    subtype: "pantry",
    wall: "back",

    widthFt: TALL_CABINET_WIDTH_FT,
    depthFt: TALL_CABINET_DEPTH_FT,
    heightFt,

    position: [
      xPosition,
      heightFt / 2,
      TALL_CABINET_DEPTH_FT / 2,
    ],

    rotation: [0, 0, 0],
    frontDirection: "front",
    generated: true,
    standardWidthIn: 24,
    heightSource:
      "derived-from-wall-height",
  };
}

function createSideTallCabinet({
  id,
  wall,
  xPosition,
  zPosition,
  wallHeightFt,
  frontDirection,
}) {
  const heightFt =
    getTallCabinetHeight(wallHeightFt);

  if (heightFt == null) return null;

  return {
    id,
    type: "tall",
    subtype: "pantry",
    wall,

    widthFt: TALL_CABINET_WIDTH_FT,
    depthFt: TALL_CABINET_DEPTH_FT,
    heightFt,

    position: [
      xPosition,
      heightFt / 2,
      zPosition,
    ],

    rotation: [
      0,
      Math.PI / 2,
      0,
    ],

    frontDirection,
    generated: true,
    standardWidthIn: 24,
    heightSource:
      "derived-from-wall-height",
  };
}

/* =========================================================
   RANGE
   ========================================================= */

function addRange({
  appliances,
  startFt,
  wallHeightFt,
}) {
  if (!supportsCountertop(wallHeightFt)) {
    return;
  }

  appliances.push({
    id: "range",
    type: "range",

    widthFt: RANGE_WIDTH_FT,
    depthFt: BASE_DEPTH_FT,
    heightFt: COUNTER_HEIGHT_FT,

    position: [
      startFt + RANGE_WIDTH_FT / 2,
      COUNTER_HEIGHT_FT / 2,
      BASE_DEPTH_FT / 2,
    ],

    rotation: [0, 0, 0],
    frontDirection: "front",
    generated: true,
    openingWidthIn: 30,
  });
}

/* =========================================================
   SINK
   ========================================================= */

function addSideSink({
  fixtures,
  id = "sink",
  xPosition,
  startFt,
  runLengthFt,
  frontDirection,
}) {
  if (runLengthFt < SINK_WIDTH_FT) {
    return;
  }

  fixtures.push({
    id,
    type: "sink",

    widthFt: SINK_WIDTH_FT,
    depthFt: 1.4,

    position: [
      xPosition,
      COUNTER_HEIGHT_FT + 0.08,
      startFt + runLengthFt / 2,
    ],

    rotation: [
      0,
      Math.PI / 2,
      0,
    ],

    frontDirection,
    generated: true,
    nominalWidthIn: 30,
  });
}

/* =========================================================
   L-SHAPE MODEL
   ========================================================= */

function buildLShapeModel(geometry) {
  const back = getWall(geometry, "back");
  const side = getWall(geometry, "side");

  if (!back || !side) {
    return emptyModel();
  }

  const cabinets = [];
  const fillers = [];
  const countertops = [];
  const appliances = [];
  const fixtures = [];

  const backWallHeight =
    getWallHeight(geometry, "back");

  const sideWallHeight =
    getWallHeight(geometry, "side");

  /* BACK WALL RANGE OPENING */

  const rangeWidth = Math.min(
    RANGE_WIDTH_FT,
    back.lengthFt
  );

  const rangeStart = Math.max(
    0,
    back.lengthFt / 2 -
      rangeWidth / 2
  );

  const leftRunLength =
    Math.max(rangeStart, 0);

  const rightRunStart =
    rangeStart + rangeWidth;

  const rightRunLength = Math.max(
    back.lengthFt - rightRunStart,
    0
  );

  /* BACK LEFT RUN */

  if (leftRunLength >= 1) {
    addBackBaseModules({
      cabinets,
      fillers,
      idPrefix: "back-base-left",
      startFt: 0,
      runLengthFt: leftRunLength,
      wallHeightFt: backWallHeight,
    });

    addBackUpperModules({
      cabinets,
      fillers,
      idPrefix: "back-upper-left",
      startFt: 0,
      runLengthFt: leftRunLength,
      wallHeightFt: backWallHeight,
    });

    if (supportsCountertop(backWallHeight)) {
      addBackCounter({
        countertops,
        id: "back-counter-left",
        startFt: 0,
        runLengthFt: leftRunLength,
      });
    }
  }

  /* BACK RIGHT RUN */

  if (rightRunLength >= 1) {
    addBackBaseModules({
      cabinets,
      fillers,
      idPrefix: "back-base-right",
      startFt: rightRunStart,
      runLengthFt: rightRunLength,
      wallHeightFt: backWallHeight,
    });

    addBackUpperModules({
      cabinets,
      fillers,
      idPrefix: "back-upper-right",
      startFt: rightRunStart,
      runLengthFt: rightRunLength,
      wallHeightFt: backWallHeight,
    });

    if (supportsCountertop(backWallHeight)) {
      addBackCounter({
        countertops,
        id: "back-counter-right",
        startFt: rightRunStart,
        runLengthFt: rightRunLength,
      });
    }
  }

  addRange({
    appliances,
    startFt: rangeStart,
    wallHeightFt: backWallHeight,
  });

  /* SIDE WALL */

  const sideStart = Math.min(
    CORNER_RESERVE_FT,
    side.lengthFt
  );

  const sideRunStart = sideStart;

  let sideRunLength = Math.max(
    side.lengthFt - sideRunStart,
    0
  );

  /* OPTIONAL PANTRY */

  if (
    canFitPantry({
      runLengthFt: sideRunLength,
      wallHeightFt: sideWallHeight,
    })
  ) {
    const pantryZ =
      sideRunStart +
      sideRunLength -
      TALL_CABINET_WIDTH_FT / 2;

    const pantry =
      createSideTallCabinet({
        id: "side-pantry",
        wall: "side",
        xPosition:
          BASE_DEPTH_FT / 2,
        zPosition: pantryZ,
        wallHeightFt:
          sideWallHeight,
        frontDirection: "front",
      });

    if (pantry) {
      cabinets.push(pantry);

      sideRunLength = Math.max(
        0,
        sideRunLength -
          TALL_CABINET_WIDTH_FT
      );
    }
  }

  /* SIDE BASE / UPPER RUN */

  if (sideRunLength >= 1) {
    addSideBaseModules({
      cabinets,
      fillers,
      idPrefix: "side-base",
      wall: "side",
      xPosition:
        BASE_DEPTH_FT / 2,
      startFt: sideRunStart,
      runLengthFt: sideRunLength,
      frontDirection: "front",
      wallHeightFt:
        sideWallHeight,
    });

    addSideUpperModules({
      cabinets,
      fillers,
      idPrefix: "side-upper",
      wall: "side",
      xPosition:
        WALL_CABINET_DEPTH_FT / 2,
      startFt: sideRunStart,
      runLengthFt: sideRunLength,
      frontDirection: "front",
      wallHeightFt:
        sideWallHeight,
    });

    if (
      supportsCountertop(
        sideWallHeight
      )
    ) {
      addSideCounter({
        countertops,
        id: "side-counter",

        // NEW metadata used by Kitchen3DView
        wall: "side",
        frontDirection: "front",

        xPosition:
          BASE_DEPTH_FT / 2,
        startFt: sideRunStart,
        runLengthFt: sideRunLength,
      });

      addSideSink({
        fixtures,
        xPosition:
          BASE_DEPTH_FT / 2,
        startFt: sideRunStart,
        runLengthFt: sideRunLength,
        frontDirection: "front",
      });
    }
  }

  return {
    cabinets,
    fillers,
    countertops,
    appliances,
    fixtures,
  };
}

/* =========================================================
   U-SHAPE MODEL
   ========================================================= */

function buildUShapeModel(geometry) {
  const back = getWall(geometry, "back");
  const left = getWall(geometry, "left");
  const right = getWall(geometry, "right");

  if (!back || !left || !right) {
    return emptyModel();
  }

  const cabinets = [];
  const fillers = [];
  const countertops = [];
  const appliances = [];
  const fixtures = [];

  const backWallHeight =
    getWallHeight(geometry, "back");

  const leftWallHeight =
    getWallHeight(geometry, "left");

  const rightWallHeight =
    getWallHeight(geometry, "right");

  /* BACK WALL */

  const usableBackStart = Math.min(
    CORNER_RESERVE_FT,
    back.lengthFt / 2
  );

  const usableBackEnd = Math.max(
    usableBackStart,
    back.lengthFt -
      CORNER_RESERVE_FT
  );

  const usableBackLength = Math.max(
    0,
    usableBackEnd -
      usableBackStart
  );

  const rangeWidth = Math.min(
    RANGE_WIDTH_FT,
    usableBackLength
  );

  const rangeStart =
    usableBackStart +
    Math.max(
      0,
      usableBackLength / 2 -
        rangeWidth / 2
    );

  const leftBackLength = Math.max(
    0,
    rangeStart -
      usableBackStart
  );

  const rightBackStart =
    rangeStart + rangeWidth;

  const rightBackLength = Math.max(
    0,
    usableBackEnd -
      rightBackStart
  );

  if (leftBackLength >= 1) {
    addBackBaseModules({
      cabinets,
      fillers,
      idPrefix: "back-base-left",
      startFt: usableBackStart,
      runLengthFt: leftBackLength,
      wallHeightFt: backWallHeight,
    });

    addBackUpperModules({
      cabinets,
      fillers,
      idPrefix: "back-upper-left",
      startFt: usableBackStart,
      runLengthFt: leftBackLength,
      wallHeightFt: backWallHeight,
    });

    if (
      supportsCountertop(
        backWallHeight
      )
    ) {
      addBackCounter({
        countertops,
        id: "back-counter-left",
        startFt: usableBackStart,
        runLengthFt: leftBackLength,
      });
    }
  }

  if (rightBackLength >= 1) {
    addBackBaseModules({
      cabinets,
      fillers,
      idPrefix: "back-base-right",
      startFt: rightBackStart,
      runLengthFt: rightBackLength,
      wallHeightFt: backWallHeight,
    });

    addBackUpperModules({
      cabinets,
      fillers,
      idPrefix: "back-upper-right",
      startFt: rightBackStart,
      runLengthFt: rightBackLength,
      wallHeightFt: backWallHeight,
    });

    if (
      supportsCountertop(
        backWallHeight
      )
    ) {
      addBackCounter({
        countertops,
        id: "back-counter-right",
        startFt: rightBackStart,
        runLengthFt: rightBackLength,
      });
    }
  }

  if (
    rangeWidth >= RANGE_WIDTH_FT
  ) {
    addRange({
      appliances,
      startFt: rangeStart,
      wallHeightFt: backWallHeight,
    });
  }

  /* LEFT WALL */

  const leftStart = Math.min(
    CORNER_RESERVE_FT,
    left.lengthFt
  );

  const leftRunLength = Math.max(
    0,
    left.lengthFt - leftStart
  );

  if (leftRunLength >= 1) {
    addSideBaseModules({
      cabinets,
      fillers,
      idPrefix: "left-base",
      wall: "left",
      xPosition:
        BASE_DEPTH_FT / 2,
      startFt: leftStart,
      runLengthFt: leftRunLength,
      frontDirection: "right",
      wallHeightFt:
        leftWallHeight,
    });

    addSideUpperModules({
      cabinets,
      fillers,
      idPrefix: "left-upper",
      wall: "left",
      xPosition:
        WALL_CABINET_DEPTH_FT / 2,
      startFt: leftStart,
      runLengthFt: leftRunLength,
      frontDirection: "right",
      wallHeightFt:
        leftWallHeight,
    });

    if (
      supportsCountertop(
        leftWallHeight
      )
    ) {
      addSideCounter({
        countertops,
        id: "left-counter",

        // NEW metadata used by Kitchen3DView
        wall: "left",
        frontDirection: "right",

        xPosition:
          BASE_DEPTH_FT / 2,
        startFt: leftStart,
        runLengthFt: leftRunLength,
      });

      addSideSink({
        fixtures,
        id: "sink",
        xPosition:
          BASE_DEPTH_FT / 2,
        startFt: leftStart,
        runLengthFt: leftRunLength,
        frontDirection: "right",
      });
    }
  }

  /* RIGHT WALL */

  const rightStart = Math.min(
    CORNER_RESERVE_FT,
    right.lengthFt
  );

  let rightRunLength = Math.max(
    0,
    right.lengthFt - rightStart
  );

  /* OPTIONAL U-SHAPE PANTRY */

  if (
    canFitPantry({
      runLengthFt: rightRunLength,
      wallHeightFt:
        rightWallHeight,
    })
  ) {
    const pantryZ =
      rightStart +
      rightRunLength -
      TALL_CABINET_WIDTH_FT / 2;

    const pantry =
      createSideTallCabinet({
        id: "right-pantry",
        wall: "right",

        xPosition:
          back.lengthFt -
          BASE_DEPTH_FT / 2,

        zPosition: pantryZ,

        wallHeightFt:
          rightWallHeight,

        frontDirection: "left",
      });

    if (pantry) {
      cabinets.push(pantry);

      rightRunLength = Math.max(
        0,
        rightRunLength -
          TALL_CABINET_WIDTH_FT
      );
    }
  }

  if (rightRunLength >= 1) {
    addSideBaseModules({
      cabinets,
      fillers,
      idPrefix: "right-base",
      wall: "right",

      xPosition:
        back.lengthFt -
        BASE_DEPTH_FT / 2,

      startFt: rightStart,
      runLengthFt: rightRunLength,

      frontDirection: "left",

      wallHeightFt:
        rightWallHeight,
    });

    addSideUpperModules({
      cabinets,
      fillers,
      idPrefix: "right-upper",
      wall: "right",

      xPosition:
        back.lengthFt -
        WALL_CABINET_DEPTH_FT / 2,

      startFt: rightStart,
      runLengthFt: rightRunLength,

      frontDirection: "left",

      wallHeightFt:
        rightWallHeight,
    });

    if (
      supportsCountertop(
        rightWallHeight
      )
    ) {
      addSideCounter({
        countertops,
        id: "right-counter",

        // NEW metadata used by Kitchen3DView
        wall: "right",
        frontDirection: "left",

        xPosition:
          back.lengthFt -
          BASE_DEPTH_FT / 2,

        startFt: rightStart,
        runLengthFt: rightRunLength,
      });
    }
  }

  return {
    cabinets,
    fillers,
    countertops,
    appliances,
    fixtures,
  };
}
